import { useCallback, useContext } from 'react';
import { useAppSelector } from 'hooks/useAppSelector';
import { CampaignDetailModel } from '@urbanx/agx-ui-components';
import { campaignsApi } from 'Api/Campaigns/campaignsApi';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';

export const createCampaignQueryKey = (campaignId: string | null): string[] => {
  if (!campaignId) return [];

  return ['campaignDetails', campaignId];
};

export const useSelectedCampaign = (): {
  selectedCampaign: CampaignDetailModel | undefined;
  isSelectedCampaignPending: boolean;
} => {
  const { selectedCampaignId } = useAppSelector((state) => state.campaigns);
  const campaignQueryKey = createCampaignQueryKey(selectedCampaignId);

  const getCampaign = useGetCampaign();

  const { data: selectedCampaign, isPending: isSelectedCampaignPending } = useQuery<
    CampaignDetailModel | undefined
  >({
    queryKey: campaignQueryKey,
    queryFn: (ctx) => getCampaign(ctx.queryKey[1] as string),
    enabled: !!selectedCampaignId && !!campaignQueryKey,
  });

  return { selectedCampaign, isSelectedCampaignPending };
};

const useGetCampaign = () => {
  const { getAuthToken } = useContext(AuthContext);

  const getCampaign = useCallback(
    async (campaignId: string | null | undefined): Promise<CampaignDetailModel | undefined> => {
      const authToken = await getAuthToken();

      if (!authToken) return undefined;

      const { data: campaignDetail } = await campaignsApi(authToken).get<CampaignDetailModel>(
        'GetCampaign',
        { campaignId: campaignId }
      );

      if (!campaignDetail) return undefined;

      return campaignDetail;
    },
    [getAuthToken]
  );

  return getCampaign;
};
